/**
 * <gnb-user-type> — resolves the KiTbetter user_type and reveals the nav entries that match it.
 *
 * Port of shopify-en-store-front app/lib/auth.ts (getKitbetterUserInfo), which ran in the
 * Hydrogen root loader on every navigation. Liquid has no request-time hook for it
 * (MIGRATION_NOTES.md #1), so it runs here.
 *
 * Same flow as the original:
 *   neither token            → user_type = ANONYMOUS, no request
 *   GET  {base}/v1/web/user/info      Authorization: Bearer <accessToken>
 *   401/403 and a refreshToken        → POST {base}/v1/web/refresh/token  header: refreshToken
 *                                       store the new pair, retry user/info
 *   data.email present       → user_id = data.userId, user_type = data.role || 'NORMAL'
 *   data.email absent        → user_type = ANONYMOUS (a guest token — see resolve())
 * `role` is 'ARTIST' | null, so user_type ends up ARTIST, NORMAL or ANONYMOUS.
 *
 * One deliberate divergence: the original shortcuts on "no access token → anonymous", which
 * misreports a live session for the five days between the two cookies expiring
 * (ACCESS_TOKEN_MAX_AGE is 2 days, REFRESH_TOKEN_MAX_AGE 7). A shopper who last visited three
 * days ago holds only the refresh token, and that state is common enough to be the normal case
 * rather than an edge one. Here the refresh token alone is a valid starting point.
 *
 * Differences forced by running in a browser rather than on the server:
 *   - The cached user_type cookie is applied before the request so nothing flashes; the
 *     response then corrects it. The original had no UI to keep steady.
 *   - Domain is only pinned to .kitbetter.com on that host. The original passes the bare
 *     hostname otherwise, which document.cookie rejects for an IP such as 127.0.0.1.
 *   - env.ENV is unavailable, so production is inferred from the hostname; data-api-base
 *     overrides the base URL either way.
 *
 * TWO THINGS CAN STOP THIS WORKING, both inherent to moving it client-side:
 *   1. accessToken / refreshToken must not be HttpOnly — document.cookie cannot read those.
 *      Check with `document.cookie` in the console.
 *   2. api.kittor.com must allow this Origin, and the Authorization header makes every call
 *      preflighted, so it must answer OPTIONS too. Expect failures on myshopify.com previews
 *      and local dev until the origins are added.
 * If either blocks it, this belongs behind an app proxy, which is what MIGRATION_NOTES.md #1
 * assumes for exactly this reason.
 */
const RELEASE_BASE_URL = 'https://api.kittor.com';
const TEST_BASE_URL = 'https://api-test.kittor.com';

const ACCESS_TOKEN_MAX_AGE = 60 * 60 * 24 * 2;
const REFRESH_TOKEN_MAX_AGE = 60 * 60 * 24 * 7;

const readCookie = (name) =>
  document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1) ?? '';

const decode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const parseTypes = (value) =>
  (value || '')
    .split(',')
    .map((type) => type.trim().toUpperCase())
    .filter(Boolean);

class GnbUserType extends HTMLElement {
  connectedCallback() {
    const { hostname } = window.location;
    this.isProduction = hostname === 'kitbetter.com' || hostname.endsWith('.kitbetter.com');
    this.baseUrl = (this.dataset.apiBase || (this.isProduction ? RELEASE_BASE_URL : TEST_BASE_URL)).replace(/\/+$/, '');
    this.accessCookieName = this.isProduction ? 'accessToken' : 'accessTokenTest';
    this.artistCookieName = this.isProduction ? 'artist_id' : 'artist_id_test';

    // Show whatever the last resolution decided, then confirm it against the API.
    this.apply(decode(readCookie('user_type')).trim().toUpperCase());

    // Anonymous means neither cookie is there — see the divergence noted at the top of the file.
    // resolve() handles the access-token-expired-but-refresh-token-alive case on its own.
    const accessToken = decode(readCookie(this.accessCookieName));
    if (!accessToken && !readCookie('refreshToken')) {
      this.markAnonymous();
      return;
    }

    this.resolve(accessToken);
  }

  /**
   * Verified, not guessed: this is only reached when there is no credential at all, when the
   * refresh token was the sole credential and it failed, or when user/info accepted the token
   * but returned no email (a guest token). All three are definitive answers, and
   * <session-sync> needs them to be marked as such — it waits on a verified event to decide
   * whether to show the page or run the SSO round trip.
   */
  markAnonymous() {
    this.writeCookie('user_type', 'ANONYMOUS');
    this.apply('ANONYMOUS', true);
  }

  /**
   * Mirrors setCookie() in the original: session cookie on path=/, pinned to .kitbetter.com
   * so the studio and the storefront share it.
   */
  writeCookie(name, value, maxAge) {
    if (value === undefined || value === null || value === '') return;
    const parts = [`${name}=${encodeURIComponent(value)}`, 'path=/', 'SameSite=Lax'];
    if (this.isProduction) parts.push('Domain=.kitbetter.com');
    if (maxAge) parts.push(`Max-Age=${maxAge}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  }

  fetchUserInfo(accessToken) {
    return fetch(`${this.baseUrl}/v1/web/user/info`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  /**
   * `accessToken` may be empty: connectedCallback lets a shopper through on the refresh token
   * alone. When it is, the user/info call that would certainly 401 is skipped and the refresh
   * below runs first.
   *
   * Giving up leaves the cached user_type alone when we started from an access token — a flaky
   * network should not flip the nav — but falls back to anonymous when the refresh token was the
   * only credential, since that is the answer the old shortcut gave for the same state.
   */
  async resolve(accessToken) {
    try {
      let result = accessToken ? await this.fetchUserInfo(accessToken) : null;

      if (!result || result.status === 401 || result.status === 403) {
        const refreshToken = decode(readCookie('refreshToken'));
        if (!refreshToken) {
          console.error('[gnb-user-type] user/info returned %s but there is no refreshToken cookie.', result?.status);
          return;
        }

        const refreshResponse = await fetch(`${this.baseUrl}/v1/web/refresh/token`, {
          method: 'POST',
          headers: { refreshToken },
        });
        if (!refreshResponse.ok) {
          console.error('[gnb-user-type] token refresh failed.', refreshResponse.status, refreshResponse.statusText);
          if (!accessToken) this.markAnonymous();
          return;
        }

        const refreshBody = await refreshResponse.json();
        const newAccessToken = refreshBody?.data?.accessToken;
        const newRefreshToken = refreshBody?.data?.refreshToken;
        if (!newAccessToken || !newRefreshToken) {
          console.error('[gnb-user-type] refresh response is missing accessToken or refreshToken.');
          if (!accessToken) this.markAnonymous();
          return;
        }

        this.writeCookie(this.accessCookieName, newAccessToken, ACCESS_TOKEN_MAX_AGE);
        this.writeCookie('refreshToken', newRefreshToken, REFRESH_TOKEN_MAX_AGE);
        result = await this.fetchUserInfo(newAccessToken);
      }

      if (!result.ok) {
        console.error('[gnb-user-type] user/info failed.', result.status, result.statusText);
        return;
      }

      const { data: userData } = await result.json();
      // A working token is not a login: the app webview injects a guest accessToken for a
      // logged-out user too, and user/info answers it with a 200. data.email is the login signal,
      // as it was in the original, so its absence is a definitive ANONYMOUS. Leaving it
      // unanswered held the inline gate's skeleton over a ready page until <session-sync>'s 3s
      // REVEAL_TIMEOUT_MS, on every store open in the app (measured 2026-10-19).
      if (!userData?.email) {
        this.markAnonymous();
        return;
      }

      const userType = (userData.role || 'NORMAL').toUpperCase();
      this.writeCookie('user_id', userData.userId);
      this.writeCookie('user_type', userType);
      this.apply(userType, true, { userId: userData.userId, artist: this.currentArtist(userData) });
    } catch (error) {
      console.error('[gnb-user-type] unexpected error.', error);
    }
  }

  /**
   * The artist whose context the shopper is in, for the GTM dataLayer's artist_id /
   * artist_name. Contract copied from the KiTbetter main web app
   * (kitbetter-front src/components/Gtag/index.tsx), which sends
   * `artist_id: currentArtist.groupId` and `artist_name: currentArtist.displayName`, and only
   * when `role` is set — a plain fan gets neither key.
   *
   * That app resolves "current" by passing `?groupId=<artist_id cookie>` to user/info so the
   * API returns the selected artist. This one does not add the parameter: the same request
   * decides the nav gates, and a stale or invalid groupId in the cookie must not be able to
   * fail it. The selection is applied to the array that comes back instead, which gives the
   * same answer whenever the cookie is valid and falls back to the first artist when it is not.
   *
   * `role` is 'ARTIST' | 'LABEL' | null (kitbetter-front src/types/api/account.ts). A LABEL
   * account can have an empty `artists`, hence the null return rather than a half-filled pair.
   */
  currentArtist(userData) {
    if (!userData || !userData.role) return null;

    const artists = Array.isArray(userData.artists) ? userData.artists : [];
    if (artists.length === 0) return null;

    const selectedId = decode(readCookie(this.artistCookieName));
    return artists.find((artist) => artist && artist.groupId === selectedId) || artists[0];
  }

  /**
   * Markup contract, both attributes taking a comma-separated list of types:
   *   [data-user-type-allow="ARTIST"]  hidden unless the resolved type matches
   *   [data-user-type-deny="ARTIST"]   hidden when it matches
   * Elements ship with `hidden` already set iff they should start hidden, so the common case
   * does not flash. The event lets anything built from user_type react to the async result.
   *
   * `verified` separates the cached answer applied on the way in from the one the API
   * confirmed. Both reach the nav gates — a cached type is the whole point of not flashing —
   * but a listener that acts on the answer rather than displaying it needs to tell them
   * apart. <session-sync> is one: it turns a KiTbetter session into a Shopify one and must
   * not do that on a stale cookie. See assets/script-session-sync.js.
   *
   * The verified answer is also left on <html> as data-user-type-verified. An event reaches only
   * the listeners already attached, and this one can fire before a later deferred script has
   * attached its own — the API can answer before script-session-sync.js has even downloaded.
   * A listener that must not miss it reads the attribute when it starts.
   */
  apply(userType, verified = false, detail = {}) {
    document.querySelectorAll('[data-user-type-allow]').forEach((element) => {
      element.hidden = !parseTypes(element.dataset.userTypeAllow).includes(userType);
    });

    document.querySelectorAll('[data-user-type-deny]').forEach((element) => {
      element.hidden = parseTypes(element.dataset.userTypeDeny).includes(userType);
    });

    document.documentElement.dataset.userType = userType;
    if (verified) document.documentElement.dataset.userTypeVerified = userType;
    document.dispatchEvent(
      new CustomEvent('gnb:usertype', {
        detail: {
          userType,
          verified,
          // Only the verified call carries these; assets/script-datalayer.js needs the id and
          // the artist pair from the API response, not from the cache applied on the way in.
          userId: detail.userId,
          artistId: detail.artist ? detail.artist.groupId : undefined,
          artistName: detail.artist ? detail.artist.displayName : undefined,
        },
      })
    );
  }
}

customElements.define('gnb-user-type', GnbUserType);

// The header is re-rendered on its own in the theme editor.
document.addEventListener('shopify:section:load', () => {
  document.querySelector('gnb-user-type')?.connectedCallback();
});
